'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Phone, ArrowRight, ChevronLeft, Shield, Copy, CheckCircle } from 'lucide-react';

type Step = 'method' | 'phone' | 'otp' | 'success';

interface PhoneForm {
  phone: string;
}

interface OTPForm {
  otp: string;
}

const DEMO_CREDENTIALS = [
  { role: 'Homemaker', phone: '+91 98765 43210', otp: '142857' },
  { role: 'Trader', phone: '+91 87654 32109', otp: '246810' },
  { role: 'Admin', phone: '+91 76543 21098', otp: '369123' },
];

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-muted-foreground hover:text-primary transition-colors" aria-label={`Copy ${value}`}>
      {copied ? <CheckCircle size={14} className="text-drop-green" /> : <Copy size={14} />}
    </button>
  );
}

export default function SignUpLoginPage() {
  const [lang, setLang] = useState<'en' | 'ta'>('en');
  const [step, setStep] = useState<Step>('method');
  const [phoneValue, setPhoneValue] = useState('');
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [loadingOTP, setLoadingOTP] = useState(false);

  const phoneForm = useForm<PhoneForm>();
  const otpForm = useForm<OTPForm>();

  const handleSendOTP = (data: PhoneForm) => {
    setLoadingPhone(true);
    setPhoneValue(data.phone);
    // Backend integration point: send OTP via SMS service
    setTimeout(() => {
      setLoadingPhone(false);
      setStep('otp');
      toast.success('OTP sent to ' + data.phone);
    }, 1500);
  };

  const handleVerifyOTP = (data: OTPForm) => {
    setLoadingOTP(true);
    // Backend integration point: verify OTP with auth service
    setTimeout(() => {
      setLoadingOTP(false);
      if (data.otp === '142857' || data.otp === '246810' || data.otp === '369123') {
        setStep('success');
      } else {
        otpForm.setError('otp', { message: 'Invalid OTP — use the demo credentials below' });
        toast.error('Invalid OTP — use the demo accounts below to sign in');
      }
    }, 1500);
  };

  const autofillCredential = (cred: typeof DEMO_CREDENTIALS[0]) => {
    phoneForm.setValue('phone', cred.phone.replace(/\s/g, '').replace('+91', ''));
    if (step === 'otp') otpForm.setValue('otp', cred.otp);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 hero-gradient flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 30% 70%, var(--green-mid), transparent 60%), radial-gradient(ellipse at 70% 30%, var(--accent), transparent 60%)' }} />
        <div className="relative z-10 text-center max-w-md">
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 bg-primary rounded-4xl flex items-center justify-center shadow-float">
              <AppLogo size={56} />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4 leading-tight">
            Koyambedu Today
          </h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            {lang === 'en' ?'Fresh Market Intelligence, Every Day. Know before you go to the market.' :'தினமும் புதிய சந்தை தகவல். சந்தைக்கு செல்வதற்கு முன் தெரிந்துக்கொள்ளுங்கள்.'}
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { icon: '🌿', stat: '45+', label: 'Products' },
              { icon: '📊', stat: 'Daily', label: 'Updates' },
              { icon: '🔔', stat: 'Smart', label: 'Alerts' },
            ].map(item => (
              <div key={`brand-stat-${item.label}`} className="glass-card rounded-2xl p-4">
                <div className="text-2xl mb-1">{item.icon}</div>
                <p className="font-bold text-foreground">{item.stat}</p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={40} />
              <span className="text-xl font-bold text-foreground">Koyambedu Today</span>
            </div>
            <p className="text-sm text-muted-foreground">Fresh Market Intelligence, Every Day</p>
          </div>

          {/* Lang toggle */}
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setLang(l => l === 'en' ? 'ta' : 'en')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary border border-border text-sm font-semibold text-primary transition-all hover:bg-green-light"
            >
              {lang === 'en' ? 'தமிழ்' : 'English'}
            </button>
          </div>

          {step === 'method' && (
            <div className="slide-up">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {lang === 'en' ? 'Welcome back' : 'மீண்டும் வரவேற்கிறோம்'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {lang === 'en' ? 'Sign in to access your watchlist and alerts.' : 'உங்கள் கண்காணிப்பு பட்டியலை அணுக உள்நுழைக.'}
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => setStep('phone')}
                  className="w-full flex items-center gap-4 p-4 card-base card-hover text-left group"
                >
                  <div className="w-12 h-12 bg-secondary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
                    <Phone size={20} className="text-primary group-hover:text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">
                      {lang === 'en' ? 'Continue with Phone' : 'மொபைலில் தொடரவும்'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'en' ? 'OTP to your mobile number' : 'உங்கள் மொபைலுக்கு OTP'}
                    </p>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </button>

                <button
                  onClick={() => toast('Google sign-in coming soon!')}
                  className="w-full flex items-center gap-4 p-4 card-base card-hover text-left group"
                >
                  <div className="w-12 h-12 bg-red-light rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">G</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">
                      {lang === 'en' ? 'Continue with Google' : 'Google மூலம் தொடரவும்'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'en' ? 'Quick and secure sign in' : 'விரைவான மற்றும் பாதுகாப்பான'}
                    </p>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-8">
                {lang === 'en' ? 'By continuing, you agree to our' : 'தொடர்வதன் மூலம், நீங்கள் ஒப்புக்கொள்கிறீர்கள்'}{' '}
                <a href="#" className="text-primary underline">Terms</a>{' '}
                {lang === 'en' ? 'and' : 'மற்றும்'}{' '}
                <a href="#" className="text-primary underline">Privacy Policy</a>
              </p>
            </div>
          )}

          {step === 'phone' && (
            <div className="slide-up">
              <button
                onClick={() => setStep('method')}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <ChevronLeft size={16} />
                {lang === 'en' ? 'Back' : 'திரும்பு'}
              </button>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {lang === 'en' ? 'Enter your phone' : 'மொபைல் எண் உள்ளிடவும்'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {lang === 'en' ? "We'll send a one-time password to verify." : 'சரிபார்க்க ஒரு முறை கடவுச்சொல் அனுப்புவோம்.'}
              </p>

              <form onSubmit={phoneForm.handleSubmit(handleSendOTP)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {lang === 'en' ? 'Mobile Number' : 'மொபைல் எண்'}
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-4 bg-muted border border-border rounded-2xl text-sm font-semibold text-foreground">
                      🇮🇳 +91
                    </div>
                    <input
                      type="tel"
                      placeholder="98765 43210"
                      className="input-base flex-1"
                      {...phoneForm.register('phone', {
                        required: lang === 'en' ? 'Phone number is required' : 'மொபைல் எண் தேவை',
                        pattern: { value: /^\d{10}$/, message: lang === 'en' ? 'Enter a valid 10-digit number' : 'சரியான 10 இலக்க எண் உள்ளிடவும்' }
                      })}
                    />
                  </div>
                  {phoneForm.formState.errors.phone && (
                    <p className="text-xs text-rise-red mt-1.5">{phoneForm.formState.errors.phone.message}</p>
                  )}
                </div>

                <button type="submit" disabled={loadingPhone} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loadingPhone ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {lang === 'en' ? 'Sending OTP...' : 'OTP அனுப்புகிறோம்...'}
                    </>
                  ) : (
                    <>
                      {lang === 'en' ? 'Send OTP' : 'OTP அனுப்பு'}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {step === 'otp' && (
            <div className="slide-up">
              <button
                onClick={() => setStep('phone')}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <ChevronLeft size={16} />
                {lang === 'en' ? 'Change number' : 'எண் மாற்று'}
              </button>
              <div className="flex items-center gap-2 mb-2">
                <Shield size={20} className="text-primary" />
                <h2 className="text-2xl font-bold text-foreground">
                  {lang === 'en' ? 'Enter OTP' : 'OTP உள்ளிடவும்'}
                </h2>
              </div>
              <p className="text-muted-foreground mb-8">
                {lang === 'en' ? `Sent to +91 ${phoneValue}` : `+91 ${phoneValue} க்கு அனுப்பப்பட்டது`}
              </p>

              <form onSubmit={otpForm.handleSubmit(handleVerifyOTP)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {lang === 'en' ? '6-digit OTP' : '6 இலக்க OTP'}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="• • • • • •"
                    className="input-base text-center text-2xl tracking-widest font-bold"
                    {...otpForm.register('otp', {
                      required: 'OTP is required',
                      minLength: { value: 6, message: 'Enter the 6-digit OTP' }
                    })}
                  />
                  {otpForm.formState.errors.otp && (
                    <p className="text-xs text-rise-red mt-1.5">{otpForm.formState.errors.otp.message}</p>
                  )}
                </div>

                <button type="submit" disabled={loadingOTP} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loadingOTP ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {lang === 'en' ? 'Verifying...' : 'சரிபார்க்கிறோம்...'}
                    </>
                  ) : (
                    lang === 'en' ? 'Verify & Sign In' : 'சரிபார்த்து உள்நுழைக'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => toast('OTP resent!')}
                  className="w-full text-sm text-primary font-semibold hover:underline"
                >
                  {lang === 'en' ? 'Resend OTP' : 'OTP மீண்டும் அனுப்பு'}
                </button>
              </form>
            </div>
          )}

          {step === 'success' && (
            <div className="slide-up text-center">
              <div className="w-20 h-20 bg-green-light rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {lang === 'en' ? 'Welcome to Koyambedu Today!' : 'கோயம்பேடு டுடேக்கு வரவேற்கிறோம்!'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {lang === 'en' ? "You're all set. Check today's market prices." : 'தயாராகிவிட்டீர்கள். இன்றைய சந்தை விலைகளை பாருங்கள்.'}
              </p>
              <Link href="/" className="btn-primary inline-flex items-center gap-2">
                {lang === 'en' ? "See Today's Market" : 'இன்றைய சந்தை பாருங்கள்'}
                <ArrowRight size={16} />
              </Link>
            </div>
          )}

          {/* Demo Credentials */}
          {(step === 'phone' || step === 'otp') && (
            <div className="mt-8 p-4 bg-amber-light border border-amber-200 rounded-2xl">
              <p className="text-xs font-semibold text-amber-700 mb-3">
                🔑 Demo Credentials — Click to autofill
              </p>
              <div className="space-y-2">
                {DEMO_CREDENTIALS.map(cred => (
                  <div key={`cred-${cred.role}`} className="flex items-center justify-between bg-white/60 rounded-xl px-3 py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-foreground w-20">{cred.role}</span>
                      <span className="text-xs text-muted-foreground font-tabular">{cred.phone}</span>
                      <span className="text-xs text-muted-foreground font-tabular">OTP: {cred.otp}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CopyButton value={cred.otp} />
                      <button
                        onClick={() => autofillCredential(cred)}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        Use
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}